import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { getRequestData } from "../../Utility/apiHelper";
import './Lead.css';

function ViewLeadSummary({ leadId }) {
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (leadId) {
      fetchLeadSummary();
    }
  }, [leadId]);

  const fetchLeadSummary = async () => {
    if (!leadId) {
      setError("Lead ID is required");
      return;
    }

    setLoading(true);
    setSummaryData(null);
    setError("");
    try {
      const apiUrl = process.env.REACT_APP_AIML_API_URL || 'http://localhost:5001';

    const response = await getRequestData(
        `${apiUrl}/api/lead-summary?lead_id=${leadId}`
    );

    setSummaryData(response.data);
    } catch (error) {
      const msg = error.response?.data?.error || "Something went wrong";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!summaryData) {
    return (
      <Box p={2}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  // Parse the summary text and format it nicely
  const formatSummary = (summaryText) => {
    if (!summaryText) return null;
    
    // Split by newlines and process each line
    const lines = summaryText.split('\n').filter(line => line.trim());
    const formattedLines = [];
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Check if it's a header (Lead Summary:)
      if (trimmedLine.startsWith('Lead Summary:')) {
        formattedLines.push({
          type: 'header',
          content: trimmedLine.replace('Lead Summary:', '').trim()
        });
      }
      // Check if it's a bullet point (starts with - or •)
      else if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
        formattedLines.push({
          type: 'bullet',
          content: trimmedLine.replace(/^[-•]\s*/, '')
        });
      }
      // Regular text
      else if (trimmedLine) {
        formattedLines.push({
          type: 'text',
          content: trimmedLine
        });
      }
    });
    
    return formattedLines;
  };

  const formattedSummary = formatSummary(summaryData.summary);

  return (
    <Box className="lead-summary-root-box">
      <div className="lead-summary-column-wrapper">
        {/* Header */}
        {formattedSummary && formattedSummary.length > 0 && formattedSummary[0].type === 'header' && (
          <Box className="lead-summary-header-box">
            <Typography variant="h5" fontWeight="bold">
              {formattedSummary[0].content}
            </Typography>
          </Box>
        )}

        {/* Summary Content */}
        {formattedSummary && formattedSummary.length > 1 && (
          <Box className="lead-summary-content-box">
            {formattedSummary.slice(1).map((item, index) => {
              if (item.type === 'bullet') {
                return (
                  <Box key={index} className="lead-summary-bullet-item">
                    <Typography 
                      variant="body1" 
                      className="lead-summary-bullet-text"
                    >
                      • {item.content}
                    </Typography>
                  </Box>
                );
              } else if (item.type === 'text') {
                return (
                  <Typography key={index} variant="body2" className="lead-summary-body-text">
                    {item.content}
                  </Typography>
                );
              }
              return null;
            })}
          </Box>
        )}

        {/* Fallback: Show raw summary if parsing didn't work */}
        {(!formattedSummary || formattedSummary.length === 0) && (
          <Box className="lead-summary-content-box">
            <Typography 
              variant="body1" 
              className="lead-summary-bullet-text"
            >
              {summaryData.summary}
            </Typography>
          </Box>
        )}
      </div>
    </Box>
  );
}

export default ViewLeadSummary;

